import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FileSpreadsheet, RefreshCw, X } from "lucide-react";
import { ENV, tbGetJson } from "../api/client";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FULL_ACCESS_ROLES = ["dtv ops admin", "employee supplier admin"];

const clean = (value) => String(value ?? "").trim();

function detailsEndpoint() {
    if (ENV.BULK_UPLOAD_DETAILS_URL) return ENV.BULK_UPLOAD_DETAILS_URL;
    return ENV.TB_BASE ? `${ENV.TB_BASE.replace(/\/$/, "")}/npw-bulkupload-details` : "/npw-bulkupload-details";
}

function formatDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
        date: date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }),
        time: date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    };
}

function TruncCell({ value, className, label }) {
    const text = clean(value) || "-";
    return (
        <td className={className} data-label={label}>
            <span className="bulk-trunc" title={text}>{text}</span>
        </td>
    );
}

const formatCount = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "-");

export default function NPWBulkUploadHistoryModal({ open, onClose, profile }) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 0 });
    const role = clean(profile?.submitterRole).toLowerCase();
    const isNominator = role === "nominator";
    const columnCount = isNominator ? 7 : 8;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async (targetPage, signal) => {
        const params = new URLSearchParams({ loginuserrole: role, page: String(targetPage), pageSize: String(pageSize) });
        if (!FULL_ACCESS_ROLES.includes(role) && profile?.submitterDtvid) params.set("logindtvid", profile.submitterDtvid);
        if (isNominator && profile?.vendorName) params.set("vendorName", profile.vendorName);

        setLoading(true);
        setError("");
        try {
            const response = await tbGetJson(`${detailsEndpoint()}?${params.toString()}`, { fetchInit: { signal } });
            if (signal?.aborted) return;
            if (response?.ok === false) throw new Error(clean(response?.message) || "Unable to load uploaded files.");
            setRows(Array.isArray(response?.data) ? response.data : []);
            setPagination({
                totalRecords: Number(response?.pagination?.totalRecords) || 0,
                totalPages: Number(response?.pagination?.totalPages) || 0,
            });
        } catch (err) {
            if (signal?.aborted || err?.name === "AbortError") return;
            const body = err?.body;
            setError(clean(body && typeof body === "object" ? body.message || body.error : "") || clean(err?.message) || "Unable to load uploaded files.");
            setRows([]);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [profile, role, isNominator, pageSize]);

    useEffect(() => {
        if (!open) return undefined;
        const controller = new AbortController();
        void load(page, controller.signal);
        return () => controller.abort();
    }, [open, page, load]);

    useEffect(() => {
        if (!open) {
            setPage(1);
            setPageSize(PAGE_SIZE_OPTIONS[0]);
            return undefined;
        }
        const onKey = (event) => { if (event.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const { totalRecords, totalPages } = pagination;
    const from = totalRecords ? (page - 1) * pageSize + 1 : 0;
    const to = Math.min(page * pageSize, totalRecords);

    return (
        <div className="bulk-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="bulk-modal" role="dialog" aria-modal="true" aria-labelledby="bulk-history-title">
                <header className="bulk-modal-header">
                    <div className="bulk-panel-title">
                        <FileSpreadsheet size={20} aria-hidden="true" />
                        <div>
                            <h2 id="bulk-history-title">Uploaded files</h2>
                            <p className="bulk-modal-subtitle">
                                {FULL_ACCESS_ROLES.includes(role)
                                    ? "All bulk uploads"
                                    : profile?.vendorName ? `Uploads for ${profile.vendorName}` : "Your bulk uploads"}
                            </p>
                        </div>
                    </div>
                    <div className="bulk-modal-actions">
                        <button className="bulk-icon-button" type="button" onClick={() => load(page)} disabled={loading} aria-label="Refresh" title="Refresh">
                            <RefreshCw size={16} aria-hidden="true" className={loading ? "bulk-spin" : undefined} />
                        </button>
                        <button className="bulk-icon-button" type="button" onClick={onClose} aria-label="Close" title="Close">
                            <X size={18} aria-hidden="true" />
                        </button>
                    </div>
                </header>

                <div className="bulk-modal-body">
                    {error && <div className="bulk-inline-notice error" role="status">{error}</div>}
                    <div className="bulk-table-wrap">
                        <table className={`bulk-table${isNominator ? " no-vendor" : ""}`}>
                            <colgroup>
                                <col className="col-file" />
                                <col className="col-std" />
                                <col className="col-std" />
                                {!isNominator && <col className="col-vendor" />}
                                <col className="col-std" />
                                <col className="col-std" />
                                <col className="col-std" />
                                <col className="col-std" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th title="File name">File name</th>
                                    <th title="Uploaded by">Uploaded by</th>
                                    <th title="Role">Role</th>
                                    {!isNominator && <th title="Vendor">Vendor</th>}
                                    <th className="num" title="Total records">Total</th>
                                    <th className="num" title="Good records">Good</th>
                                    <th className="num" title="Bad records">Bad</th>
                                    <th title="Uploaded time">Uploaded</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && rows.length === 0 && (
                                    <tr><td colSpan={columnCount} className="bulk-table-empty">Loading uploaded files...</td></tr>
                                )}
                                {!loading && !error && rows.length === 0 && (
                                    <tr><td colSpan={columnCount} className="bulk-table-empty">No uploaded files found.</td></tr>
                                )}
                                {rows.map((row) => {
                                    const uploaded = formatDate(row.uploadedTime);
                                    return (
                                        <tr key={row.fileId ?? `${row.fileName}-${row.uploadedTime}`} className={loading ? "is-loading" : undefined}>
                                            <TruncCell value={row.fileName} className="bulk-file-cell" label="File name" />
                                            <td data-label="Uploaded by">
                                                <span className="bulk-trunc bulk-user-name" title={clean(row.uploadedBy) || "-"}>{clean(row.uploadedBy) || "-"}</span>
                                                {row.uploadedByEmail && <span className="bulk-trunc bulk-user-email" title={row.uploadedByEmail}>{row.uploadedByEmail}</span>}
                                            </td>
                                            <TruncCell value={row.submitterRole} label="Role" />
                                            {!isNominator && <TruncCell value={row.vendorName} label="Vendor" />}
                                            <td className="num" data-label="Total"><span className="bulk-trunc" title={formatCount(row.totalRecords)}>{formatCount(row.totalRecords)}</span></td>
                                            <td className="num" data-label="Good"><span className="bulk-pill good" title={formatCount(row.goodRecords)}>{formatCount(row.goodRecords)}</span></td>
                                            <td className="num" data-label="Bad"><span className={`bulk-pill ${Number(row.badRecords) > 0 ? "bad" : "neutral"}`} title={formatCount(row.badRecords)}>{formatCount(row.badRecords)}</span></td>
                                            <td className="bulk-date-cell" data-label="Uploaded">
                                                {uploaded ? (
                                                    <span title={`${uploaded.date} ${uploaded.time}`}>
                                                        <span className="bulk-trunc">{uploaded.date}</span>
                                                        <span className="bulk-trunc bulk-user-email">{uploaded.time}</span>
                                                    </span>
                                                ) : "-"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <footer className="bulk-modal-footer">
                    <div className="bulk-footer-info">
                        <label className="bulk-page-size">
                            Rows per page
                            <select
                                value={pageSize}
                                disabled={loading}
                                onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
                            </select>
                        </label>
                        <span>{totalRecords ? `Showing ${from}-${to} of ${totalRecords.toLocaleString()}` : ""}</span>
                    </div>
                    <div className="bulk-pager">
                        <button className="bulk-icon-button" type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={loading || page <= 1} aria-label="Previous page">
                            <ChevronLeft size={16} aria-hidden="true" />
                        </button>
                        <span>Page {totalPages ? page : 0} of {totalPages}</span>
                        <button className="bulk-icon-button" type="button" onClick={() => setPage((p) => p + 1)} disabled={loading || page >= totalPages} aria-label="Next page">
                            <ChevronRight size={16} aria-hidden="true" />
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
